// @ts-nocheck
/**
 * normalizer.ts — WP-011 Template Loader
 *
 * Optional-field normalization for a structurally valid TemplateDocument.
 *
 * Contract:
 *   - Returns a NEW object; never mutates input.
 *   - Fills missing optional collection fields with [].
 *   - Deterministic: same input always produces structurally identical output.
 *   - Does NOT generate or modify IDs.
 *   - Does NOT introduce live values (e.g. timestamps are set to '' sentinel,
 *     not Date.now()).
 *
 * Pure TypeScript — no framework dependencies, no side effects.
 */

import type {
  CategoryBundle,
  ComponentNode,
  ElementDefinition,
  ElementLayout,
  PropertyDefinition,
  TemplateDocument,
} from '../../types/canonical';
import { isValueSourceShape, unwrapValueSource } from '../binding/bindingContract';
import { stripFileTableSchemaBallast } from '../table/fileTableCsv';
import { migrateTablePayloadInPlace } from '../renderer/tablePayloadMigration';
import { flattenTemplateClasses } from './flattenTemplateClasses';


// ── Internal helpers ──────────────────────────────────────────────────────────

/** One-way import repair: hoist button.label.source → button.label (DEC-036). */
function normalizeComponent(component: ComponentNode): ComponentNode {
  if (component.type === 'button') {
    const rawLabel = component.button.label as unknown;
    if (!isValueSourceShape(rawLabel)) {
      const unwrapped = unwrapValueSource<string>(rawLabel);
      if (unwrapped) {
        return {
          ...component,
          button: {
            ...component.button,
            label: unwrapped,
          },
        };
      }
    }
  }
  if (component.type === 'table') {
    const table = { ...(component.table as unknown as Record<string, unknown>) };
    migrateTablePayloadInPlace(table);
    return {
      ...component,
      table,
    } as ComponentNode;
  }
  if (component.type === 'group') {
    return {
      ...component,
      group: {
        ...component.group,
        children: component.group.children.map(normalizeComponent),
      },
    };
  }
  return component;
}

function normalizeLayout(layout: ElementLayout): ElementLayout {
  return {
    ...layout,
    components: layout.components.map(normalizeComponent),
  };
}

function normalizeElement(element: ElementDefinition): ElementDefinition {
  return {
    ...element,
    // categoryIds is required by the type but normalizer is defensive at runtime
    categoryIds: Array.isArray(element.categoryIds) ? element.categoryIds : [],
    layouts: element.layouts.map(normalizeLayout),
  };
}

function normalizeProperty(property: PropertyDefinition): PropertyDefinition {
  if (property.type !== 'fileTable' || !property.validation?.tableSchema) {
    return property;
  }
  return {
    ...property,
    validation: {
      ...property.validation,
      tableSchema: stripFileTableSchemaBallast(property.validation.tableSchema),
    },
  };
}

function normalizeCategoryBundle(bundle: CategoryBundle): CategoryBundle {
  return {
    ...bundle,
    // categories and properties are required by the type but normalizer is defensive
    categories: Array.isArray(bundle.categories) ? bundle.categories : [],
    properties: Array.isArray(bundle.properties)
      ? bundle.properties.map(normalizeProperty)
      : [],
  };
}

// ── Public API ────────────────────────────────────────────────────────────────

/**
 * Returns a new TemplateDocument with all optional collection fields filled
 * with their default empty values. Does not mutate input. Deterministic for
 * same input. Applies DEC-066 class flatten after structural repairs.
 */
export function normalizeDocument(document: TemplateDocument): TemplateDocument {
  const normalized: TemplateDocument = {
    ...document,
    metadata: {
      ...document.metadata,
      tags: document.metadata.tags ?? [],
      // Use empty string as sentinel — normalizer must not produce live timestamps
      createdAt: document.metadata.createdAt ?? '',
    },
    elements: document.elements.map(normalizeElement),
    categories: document.categories.map(normalizeCategoryBundle),
  };
  return flattenTemplateClasses(normalized);
}
